<template>
  <BasicTemplate>
    <v-card>
      <v-card-title class="d-flex flex-wrap align-center">
        <v-btn icon class="mr-2" exact :to="{ path: '/syndication-config' }">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="text-h6">Partner UI customization</div>
        </div>
        <v-chip v-if="domainLabel" small class="ml-3" outlined>{{ domainLabel }}</v-chip>
      </v-card-title>

      <v-card-text>
        <Loader v-if="loading" />

        <template v-else>
          <v-alert v-if="loadError" type="error" class="mb-4" dismissible @click:close="loadError = ''">
            {{ loadError }}
          </v-alert>

          <v-alert v-else-if="invalidIndex" type="warning" class="mb-4">
            Invalid partner index.
            <router-link to="/syndication-config">Back to Syndication Partners</router-link>
          </v-alert>

          <v-alert v-else-if="!partner" type="warning" class="mb-4">
            Partner not found (list may have changed).
            <router-link to="/syndication-config">Back to Syndication Partners</router-link>
          </v-alert>

          <template v-else>
            <v-alert v-if="formError" type="error" dense text class="mb-4" dismissible @click:close="formError = ''">
              {{ formError }}
            </v-alert>

            <v-row dense align="stretch" class="partner-ui-main-row">
              <!-- Settings left; preview right — fits one viewport on desktop -->
              <v-col cols="12" md="4" lg="3" class="partner-ui-settings-col">
                <div class="partner-ui-settings-stack partner-ui-compact-form">
                <v-card outlined class="pa-2 mb-0">
                  <v-card-subtitle class="partner-ui-card-head px-0 pt-0 text-subtitle-2 font-weight-medium">
                    CTA screen
                  </v-card-subtitle>
                  <v-switch
                    v-model="ctaScreenForm.backgroundBlurEnabled"
                    label="Background blur"
                    hide-details
                    dense
                    class="mt-0"
                  />
                  <div class="d-flex align-start flex-wrap syndication-color-row mt-1">
                    <v-text-field
                      v-model="ctaScreenForm.backgroundColor"
                      label="Background color"
                      outlined
                      dense
                      hide-details="auto"
                      class="flex-grow-1 mr-2 syndication-color-hex"
                    />
                    <v-text-field
                      :value="hexForColorInput(ctaScreenForm.backgroundColor)"
                      outlined
                      dense
                      hide-details
                      type="color"
                      aria-label="Pick background color (CTA screen)"
                      class="syndication-color-picker"
                      @input="ctaScreenForm.backgroundColor = $event"
                    />
                  </div>
                  <div class="d-flex align-start flex-wrap syndication-color-row mt-1">
                    <v-text-field
                      v-model="ctaScreenForm.ctaTextColor"
                      label="CTA text color"
                      outlined
                      dense
                      hide-details="auto"
                      class="flex-grow-1 mr-2 syndication-color-hex"
                    />
                    <v-text-field
                      :value="hexForColorInput(ctaScreenForm.ctaTextColor)"
                      outlined
                      dense
                      hide-details
                      type="color"
                      aria-label="Pick CTA text color"
                      class="syndication-color-picker"
                      @input="ctaScreenForm.ctaTextColor = $event"
                    />
                  </div>
                  <div class="d-flex align-start flex-wrap syndication-color-row mt-1">
                    <v-text-field
                      v-model="ctaScreenForm.ctaPlayBtnColor"
                      label="Play button color"
                      outlined
                      dense
                      hide-details="auto"
                      class="flex-grow-1 mr-2 syndication-color-hex"
                    />
                    <v-text-field
                      :value="hexForColorInput(ctaScreenForm.ctaPlayBtnColor)"
                      outlined
                      dense
                      hide-details
                      type="color"
                      aria-label="Pick play button color"
                      class="syndication-color-picker"
                      @input="ctaScreenForm.ctaPlayBtnColor = $event"
                    />
                  </div>
                  <div class="d-flex align-start flex-wrap syndication-color-row mt-1">
                    <v-text-field
                      v-model="ctaScreenForm.ctaPlayBtnTextColor"
                      label="Play button text color"
                      outlined
                      dense
                      hide-details="auto"
                      class="flex-grow-1 mr-2 syndication-color-hex"
                    />
                    <v-text-field
                      :value="hexForColorInput(ctaScreenForm.ctaPlayBtnTextColor)"
                      outlined
                      dense
                      hide-details
                      type="color"
                      aria-label="Pick play button text color"
                      class="syndication-color-picker"
                      @input="ctaScreenForm.ctaPlayBtnTextColor = $event"
                    />
                  </div>
                  <v-text-field
                    v-model="ctaScreenForm.ctaPlayBtnMaxWidth"
                    label="Play button max width (px, 80–600)"
                    outlined
                    dense
                    hide-details="auto"
                    class="mt-1"
                    hint="Number or string like 320 / 320px"
                  />
                  <v-switch
                    v-model="ctaScreenForm.ctaTitleVisible"
                    label="Title visible"
                    hide-details
                    dense
                    class="mt-1"
                  />
                  <v-switch
                    v-model="ctaScreenForm.ctaDescriptionVisible"
                    label="Description visible"
                    hide-details
                    dense
                    class="mt-1"
                  />
                  <v-select
                    v-model="ctaScreenForm.ctaTitlePlacement"
                    :items="ctaTitlePlacements"
                    label="Title placement"
                    outlined
                    dense
                    hide-details="auto"
                    class="mt-1"
                  />
                  <v-select
                    v-model="ctaScreenForm.playButtonPlacement"
                    :items="playButtonPlacements"
                    label="Play button placement"
                    outlined
                    dense
                    hide-details="auto"
                    class="mt-1"
                  />
                  <v-textarea
                    v-model="ctaScreenForm.displayAdMobileSizesJson"
                    label="Mobile ad sizes (JSON)"
                    outlined
                    dense
                    rows="1"
                    auto-grow
                    hide-details="auto"
                    class="mono-json mt-1"
                    :error-messages="displayAdMobileSizesJsonErrors"
                    hint="[[w,h]] or [w,h], max 728×300. Phone preview; changing reloads the iframe."
                    spellcheck="false"
                  />
                  <v-textarea
                    v-model="ctaScreenForm.displayAdDesktopSizesJson"
                    label="Desktop ad sizes (JSON)"
                    outlined
                    dense
                    rows="1"
                    auto-grow
                    hide-details="auto"
                    class="mono-json mt-1"
                    :error-messages="displayAdDesktopSizesJsonErrors"
                    hint="[[w,h]] or [w,h], max 728×300. PC/Tablet preview; changing reloads the iframe."
                    spellcheck="false"
                  />
                </v-card>
                <v-card outlined class="pa-2 mb-0">
                  <v-card-subtitle class="partner-ui-card-head px-0 pt-0 text-subtitle-2 font-weight-medium">
                    End screen
                  </v-card-subtitle>
                  <v-switch
                    v-model="endScreenForm.backgroundBlurEnabled"
                    label="Background blur"
                    hide-details
                    dense
                    class="mt-0"
                  />
                  <div class="d-flex align-start flex-wrap syndication-color-row mt-1">
                    <v-text-field
                      v-model="endScreenForm.backgroundColor"
                      label="Background color"
                      outlined
                      dense
                      hide-details="auto"
                      class="flex-grow-1 mr-2 syndication-color-hex"
                    />
                    <v-text-field
                      :value="hexForColorInput(endScreenForm.backgroundColor)"
                      outlined
                      dense
                      hide-details
                      type="color"
                      aria-label="Pick background color (end screen)"
                      class="syndication-color-picker"
                      @input="endScreenForm.backgroundColor = $event"
                    />
                  </div>
                  <v-switch
                    v-model="endScreenForm.useCtaBackground"
                    label="Use CTA background"
                    hide-details
                    dense
                    class="mt-1"
                  />
                </v-card>
                </div>
              </v-col>
              <v-col cols="12" md="8" lg="9">
                <v-card outlined class="partner-ui-embed-card pa-3 partner-ui-embed-sticky">
                  <v-card-subtitle class="px-0 pt-0 text-subtitle-1 font-weight-medium">Embed preview</v-card-subtitle>
                  <div
                    class="partner-ui-preview-toolbar d-flex flex-wrap align-center mb-2"
                    data-testid="syndication-customui-preview-toolbar"
                  >
                    <v-btn-toggle
                      v-model="embedPreviewDevice"
                      mandatory
                      dense
                      class="mr-3 mb-1 flex-shrink-0"
                      color="primary"
                      :disabled="!embedPreviewSrc"
                      data-testid="syndication-customui-preview-device-toggle"
                    >
                      <v-btn value="pc" small>PC</v-btn>
                      <v-btn value="tablet" small>Tablet</v-btn>
                      <v-btn value="phone" small>Phone</v-btn>
                    </v-btn-toggle>
                    <div class="partner-ui-preview-game-field flex-grow-1 mb-1">
                      <v-select
                        v-model="previewGameSlug"
                        :items="syndicationGamesForPreview"
                        item-text="name"
                        item-value="slug"
                        label="Game"
                        outlined
                        dense
                        hide-details="auto"
                        :loading="syndicationGamesLoading"
                        :disabled="!syndicationGamesForPreview.length && !syndicationGamesLoading"
                        :hint="syndicationGamesLoadError || undefined"
                        :persistent-hint="!!syndicationGamesLoadError"
                        class="partner-ui-preview-game-select mt-0 pt-0"
                        data-testid="syndication-customui-preview-game"
                      />
                    </div>
                  </div>
                  <div v-if="embedPreviewSrc" class="partner-ui-embed-preview-block">
                    <div class="partner-ui-preview-canvas">
                      <div
                        class="partner-ui-preview-viewport"
                        :class="`partner-ui-preview-viewport--${embedPreviewDevice}`"
                      >
                        <div class="partner-ui-aspect-wrap">
                          <iframe
                            ref="partnerPreviewIframe"
                            :key="iframePreviewKey"
                            :src="embedPreviewSrc"
                            title="Syndication embed preview"
                            class="partner-ui-preview-iframe"
                            data-testid="syndication-customui-preview-iframe"
                            scrolling="no"
                            referrerpolicy="no-referrer-when-downgrade"
                            @load="onPreviewIframeLoad"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <v-alert v-else dense text type="info" class="mb-0">
                    Set a valid partner domain (used as the embed origin). Bare hostnames use
                    <code>https://</code>, except <code>localhost</code> / loopback, which use <code>http://</code> (and
                    <code>:3000</code> when no port is set).
                  </v-alert>
                  <code v-if="embedPreviewSrc" class="caption d-block mt-2 text-truncate" :title="embedPreviewSrc">{{
                    embedPreviewSrc
                  }}</code>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </template>
      </v-card-text>

      <v-card-actions v-if="partner && !loading && !invalidIndex" class="flex-wrap align-center">
        <v-btn
          text
          color="warning"
          :disabled="saving"
          data-testid="syndication-customui-disable"
          @click="confirmDisableCustomUi"
        >
          Disable custom UI
        </v-btn>
        <v-btn
          small
          outlined
          class="ml-1"
          :disabled="!embedPreviewSrc || saving"
          data-testid="syndication-customui-reload-iframe"
          @click="bumpIframePreview"
        >
          Reload iframe
        </v-btn>
        <v-btn
          small
          outlined
          class="ml-1"
          :disabled="!embedPreviewSrc || saving"
          data-testid="syndication-customui-reset-preview"
          @click="resetCustomUiInPreviewIframe"
        >
          Reset preview
        </v-btn>
        <v-spacer />
        <v-btn color="primary" :loading="saving" :disabled="saving" @click="save">Save</v-btn>
        <v-btn text exact class="ml-1" :to="{ path: '/syndication-config' }">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import Loader from '../Loader/Loader.vue';
import config from '../../config';
import { ClearCacheService } from '../../services/ClearCacheService';
import { normalizeSyndicationClientsBody } from './syndicationNormalize';
import { syndicationCatalogUrlCandidates } from './syndicationCatalogUrls';

const CTA_TITLE_PLACEMENTS = ['besideThumb', 'belowThumb', 'aboveThumb'];
const PLAY_BUTTON_PLACEMENTS = ['afterThumb', 'belowThumb'];

function syndicationLocaleFromPartner(partner) {
  if (!partner || typeof partner !== 'object' || Array.isArray(partner)) {
    return 'en';
  }
  const lk = Object.keys(partner).find((k) => k.toLowerCase() === 'defaultlocale');
  if (lk != null && partner[lk] != null && String(partner[lk]).trim() !== '') {
    const t = String(partner[lk]).trim().toLowerCase();
    return (t.match(/^[a-z0-9-]+/) || ['en'])[0].slice(0, 12) || 'en';
  }
  return 'en';
}

function isLoopbackDevHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '::1';
}

/** Host (+ port) for loopback: default port 3000 when none set (dev syndication). */
function loopbackOriginHost(u) {
  if (!u || !isLoopbackDevHost(u.hostname)) {
    return u.host;
  }
  if (u.port) {
    return u.host;
  }
  if (u.hostname === '::1') {
    return '[::1]:3000';
  }
  return `${u.hostname}:3000`;
}

/** Scheme + host (+ port) for embed / feed-docs links, from the partner `domain` field. */
function originFromPartnerDomainField(d) {
  const s = d == null ? '' : String(d).trim();
  if (!s) {
    return '';
  }
  let candidate = s;
  if (!/^https?:\/\//i.test(candidate)) {
    try {
      const probe = new URL(`http://${candidate}`);
      candidate = isLoopbackDevHost(probe.hostname) ? `http://${s}` : `https://${s}`;
    } catch {
      candidate = `https://${s}`;
    }
  }
  try {
    const u = new URL(candidate);
    if (!u.host) {
      return '';
    }
    const protocol = isLoopbackDevHost(u.hostname) ? 'http:' : u.protocol;
    const host = loopbackOriginHost(u);
    return `${protocol}//${host}`;
  } catch {
    return '';
  }
}

function resolveGameIframeUrl(rawIframe, origin, locale, slug) {
  const o = origin && String(origin).replace(/\/+$/, '');
  let loc = String(locale || 'en').trim().toLowerCase();
  loc = loc.replace(/[^a-z0-9-]/g, '').slice(0, 12) || 'en';
  const fallback = `${o}/syndication/${encodeURIComponent(loc)}/games/${encodeURIComponent(slug)}`;
  const s = rawIframe == null ? '' : String(rawIframe).trim();
  if (!s) {
    return fallback;
  }
  if (/^https?:\/\//i.test(s)) {
    return s;
  }
  if (s.startsWith('//')) {
    try {
      return new URL(s, origin).href;
    } catch {
      return fallback;
    }
  }
  if (s.startsWith('/')) {
    try {
      return new URL(s, `${o}/`).href;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/** Games from syndication catalog JSON: name (label), slug (value), iframeUrl (iframe src). */
function mapSyndicationCatalogGames(games, origin, locale) {
  if (!Array.isArray(games)) {
    return [];
  }
  const items = [];
  for (let i = 0; i < games.length; i += 1) {
    const g = games[i];
    if (!g || typeof g !== 'object') {
      continue;
    }
    const slug = g.slug != null ? String(g.slug).trim() : '';
    if (!slug) {
      continue;
    }
    const name = g.name != null ? String(g.name).trim() : '';
    const iframeUrl = resolveGameIframeUrl(g.iframeUrl, origin, locale, slug);
    items.push({
      name: name || slug,
      slug,
      iframeUrl,
    });
  }
  items.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return items;
}

function findCustomUiPropertyKey(partner) {
  if (!partner || typeof partner !== 'object' || Array.isArray(partner)) {
    return null;
  }
  return Object.keys(partner).find((k) => k.toLowerCase() === 'customui') || null;
}

function valueForJsonEditor(partner) {
  const key = findCustomUiPropertyKey(partner);
  if (!key) {
    return {};
  }
  const v = partner[key];
  if (v == null) {
    return {};
  }
  if (typeof v === 'object' && !Array.isArray(v)) {
    return v;
  }
  return {};
}

function parseCustomUiObjectFromJsonText(jsonText) {
  let parsed;
  try {
    parsed = JSON.parse(jsonText != null ? String(jsonText).trim() || '{}' : '{}');
  } catch (e) {
    return { ok: false, error: 'Invalid JSON' };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {
      ok: false,
      error: 'Custom UI must be a JSON object (not an array or primitive).',
    };
  }
  return { ok: true, value: parsed };
}

function pickBool(v, defaultValue) {
  if (v === undefined || v === null) {
    return defaultValue;
  }
  return !!v;
}

function pickStr(v) {
  return v == null ? '' : String(v);
}

/** 6-char #rrggbb for HTML color input; invalid/empty → #000000. */
function hexForColorInput(raw) {
  const s = raw == null ? '' : String(raw).trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) {
    return s.toLowerCase();
  }
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#000000';
}

function stringifySizes(arr, fallbackJson) {
  if (!Array.isArray(arr)) {
    return fallbackJson;
  }
  try {
    return JSON.stringify(arr);
  } catch (e) {
    return fallbackJson;
  }
}

function parsePlacement(v, allowed, fallback) {
  const s = v == null ? '' : String(v);
  return allowed.includes(s) ? s : fallback;
}

function parseJsonSizePairs(raw, maxPairs, maxW, maxH) {
  const s = String(raw ?? '').trim();
  if (!s) {
    return null;
  }
  let arr;
  try {
    arr = JSON.parse(s);
  } catch (e) {
    return null;
  }
  if (!Array.isArray(arr)) {
    return null;
  }
  // Allow one pair as [w, h] instead of [[w, h]] (common mistake).
  if (
    arr.length === 2
    && !Array.isArray(arr[0])
    && !Array.isArray(arr[1])
    && arr[0] != null
    && arr[1] != null
  ) {
    arr = [[arr[0], arr[1]]];
  }
  if (arr.length > maxPairs) {
    return null;
  }
  const out = [];
  for (let i = 0; i < arr.length; i += 1) {
    const pair = arr[i];
    if (!Array.isArray(pair) || pair.length !== 2) {
      return null;
    }
    const w = Number(pair[0]);
    const h = Number(pair[1]);
    if (
      !Number.isInteger(w)
      || !Number.isInteger(h)
      || w <= 0
      || h <= 0
      || w > maxW
      || h > maxH
    ) {
      return null;
    }
    out.push([w, h]);
  }
  return out.length ? out : null;
}

/** Non-empty invalid JSON / bounds → one message for Vuetify error display. */
function jsonSizePairsFieldErrors(raw, maxPairs, maxW, maxH) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return [];
  }
  if (parseJsonSizePairs(raw, maxPairs, maxW, maxH)) {
    return [];
  }
  return [
    `Bad JSON or sizes — use [[w,h]] or [w,h], max ${maxW}×${maxH}, up to ${maxPairs} pairs.`,
  ];
}

/** Stable key for whether mobile/desktop ad size lists changed (iframe reload refreshes ad init). */
function adSizesFingerprintFromCtaScreen(cta) {
  if (!cta || typeof cta !== 'object') {
    return JSON.stringify({ m: null, d: null });
  }
  const m = Array.isArray(cta.displayAdMobileSizes) ? cta.displayAdMobileSizes : null;
  const d = Array.isArray(cta.displayAdDesktopSizes) ? cta.displayAdDesktopSizes : null;
  return JSON.stringify({ m, d });
}

function defaultCtaScreenForm() {
  return {
    backgroundBlurEnabled: true,
    backgroundColor: '',
    ctaTextColor: '',
    ctaPlayBtnColor: '',
    ctaPlayBtnTextColor: '',
    ctaPlayBtnMaxWidth: '',
    ctaTitleVisible: true,
    ctaDescriptionVisible: true,
    ctaTitlePlacement: 'besideThumb',
    playButtonPlacement: 'afterThumb',
    displayAdMobileSizesJson: '[[300, 50]]',
    displayAdDesktopSizesJson: '[[300, 250], [300, 50]]',
  };
}

function defaultEndScreenForm() {
  return {
    backgroundBlurEnabled: true,
    backgroundColor: '',
    useCtaBackground: false,
  };
}

export default {
  name: 'SyndicationPartnerCustomUiPage',
  components: { BasicTemplate, Loader },
  props: {
    partnerIndex: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      loading: true,
      loadError: '',
      clients: [],
      jsonText: '{}',
      formError: '',
      saving: false,
      syncFormFromJson: false,
      ctaScreenForm: defaultCtaScreenForm(),
      endScreenForm: defaultEndScreenForm(),
      ctaTitlePlacements: CTA_TITLE_PLACEMENTS,
      playButtonPlacements: PLAY_BUTTON_PLACEMENTS,
      previewGameSlug: 'blackjack',
      embedPreviewDevice: 'pc',
      iframePreviewKey: 0,
      _rebuildTimer: null,
      syndicationGamesForPreview: [],
      syndicationGamesLoading: false,
      syndicationGamesLoadError: '',
      _syndicationCatalogFetchTimer: null,
      /** Raw `games` from last successful GET …/syndication — remap only when locale changes (no second network request). */
      _syndicationCatalogGamesRaw: null,
      /** Cancels in-flight catalog fetch when a newer one starts (replaces seq-based invalidation after await). */
      _syndicationCatalogAbortController: null,
      /** Last serialized ad size lists pushed to preview; when they change we remount the iframe so the embed re-inits ads. */
      _lastPreviewAdSizesFingerprint: null,
    };
  },
  computed: {
    invalidIndex() {
      return !Number.isInteger(this.partnerIndex) || this.partnerIndex < 0;
    },
    partner() {
      if (this.invalidIndex) {
        return null;
      }
      return this.clients[this.partnerIndex] || null;
    },
    domainLabel() {
      const p = this.partner;
      if (!p || typeof p !== 'object') {
        return '';
      }
      const dk = Object.keys(p).find((k) => k.toLowerCase() === 'domain');
      const d = dk ? p[dk] : '';
      return d == null ? '' : String(d).trim();
    },
    embedPreviewLocale() {
      return syndicationLocaleFromPartner(this.partner);
    },
    embedPreviewSrc() {
      let slug = String(this.previewGameSlug || '').trim().toLowerCase();
      slug = slug.replace(/[^a-z0-9_-]/g, '');
      const list = Array.isArray(this.syndicationGamesForPreview) ? this.syndicationGamesForPreview : [];
      if (slug) {
        const hit = list.find((g) => g && g.slug === slug);
        if (hit && hit.iframeUrl) {
          const u = String(hit.iframeUrl).trim();
          if (/^https?:\/\//i.test(u)) {
            return u;
          }
        }
      }
      const base = originFromPartnerDomainField(this.domainLabel);
      if (!base) {
        return '';
      }
      let loc = String(this.embedPreviewLocale || 'en').trim().toLowerCase();
      loc = loc.replace(/[^a-z0-9-]/g, '').slice(0, 12) || 'en';
      if (!slug) {
        slug = 'blackjack';
      }
      return `${base}/syndication/${encodeURIComponent(loc)}/games/${encodeURIComponent(slug)}`;
    },
    embedPreviewOrigin() {
      const s = this.embedPreviewSrc;
      if (!s || typeof s !== 'string') {
        return '';
      }
      try {
        return new URL(s).origin;
      } catch {
        return '';
      }
    },
    displayAdMobileSizesJsonErrors() {
      return jsonSizePairsFieldErrors(this.ctaScreenForm.displayAdMobileSizesJson, 12, 728, 300);
    },
    displayAdDesktopSizesJsonErrors() {
      return jsonSizePairsFieldErrors(this.ctaScreenForm.displayAdDesktopSizesJson, 12, 728, 300);
    },
  },
  watch: {
    partner: {
      handler(p) {
        if (!p) {
          this.jsonText = '{}';
          this.syndicationGamesForPreview = [];
          this.syndicationGamesLoadError = '';
          this._syndicationCatalogGamesRaw = null;
          this.$nextTick(() => this.hydrateFormFromJsonText());
          return;
        }
        const raw = valueForJsonEditor(p);
        this.jsonText = JSON.stringify(raw, null, 2);
        this.$nextTick(() => {
          this.hydrateFormFromJsonText();
          this.scheduleSyndicationCatalogFetch();
        });
      },
      immediate: true,
    },
    ctaScreenForm: {
      deep: true,
      handler() {
        if (this.syncFormFromJson) {
          return;
        }
        this.scheduleRebuildFromForm();
      },
    },
    endScreenForm: {
      deep: true,
      handler() {
        if (this.syncFormFromJson) {
          return;
        }
        this.scheduleRebuildFromForm();
      },
    },
    domainLabel() {
      this.scheduleSyndicationCatalogFetch();
    },
    embedPreviewDevice() {
      this.$nextTick(() => {
        this.pushCustomUiToPreviewIframe();
      });
    },
    embedPreviewLocale() {
      this.bumpIframePreview();
      const origin = originFromPartnerDomainField(this.domainLabel);
      if (
        origin
        && this._syndicationCatalogGamesRaw
        && Array.isArray(this._syndicationCatalogGamesRaw)
      ) {
        this.syndicationGamesForPreview = mapSyndicationCatalogGames(
          this._syndicationCatalogGamesRaw,
          origin,
          this.embedPreviewLocale,
        );
        return;
      }
      this.scheduleSyndicationCatalogFetch();
    },
    previewGameSlug() {
      this.bumpIframePreview();
    },
  },
  created() {
    this.reload();
  },
  beforeDestroy() {
    if (this._rebuildTimer) {
      clearTimeout(this._rebuildTimer);
    }
    if (this._syndicationCatalogFetchTimer) {
      clearTimeout(this._syndicationCatalogFetchTimer);
    }
    if (this._syndicationCatalogAbortController) {
      this._syndicationCatalogAbortController.abort();
      this._syndicationCatalogAbortController = null;
    }
  },
  methods: {
    hexForColorInput,
    httpErrorMessage(err) {
      if (err && err.body) {
        if (typeof err.body === 'object' && err.body.message) {
          return String(err.body.message);
        }
        if (typeof err.body === 'string' && err.body.trim()) {
          return err.body.trim();
        }
      }
      return (err && err.statusText) || 'Request failed';
    },
    scheduleRebuildFromForm() {
      if (this._rebuildTimer) {
        clearTimeout(this._rebuildTimer);
      }
      this._rebuildTimer = setTimeout(() => {
        this._rebuildTimer = null;
        this.rebuildJsonFromForm();
        const cta = this.serializeCtaScreen();
        const fp = adSizesFingerprintFromCtaScreen(cta);
        if (this._lastPreviewAdSizesFingerprint === null) {
          this._lastPreviewAdSizesFingerprint = fp;
          this.pushCustomUiToPreviewIframe();
        } else if (fp !== this._lastPreviewAdSizesFingerprint) {
          this._lastPreviewAdSizesFingerprint = fp;
          this.bumpIframePreview();
        } else {
          this.pushCustomUiToPreviewIframe();
        }
      }, 120);
    },
    hydrateFormFromJsonText() {
      const r = parseCustomUiObjectFromJsonText(this.jsonText);
      const root = r.ok && r.value ? r.value : {};
      const c = root.ctaScreen && typeof root.ctaScreen === 'object' && !Array.isArray(root.ctaScreen)
        ? root.ctaScreen
        : {};
      const e = root.endScreen && typeof root.endScreen === 'object' && !Array.isArray(root.endScreen)
        ? root.endScreen
        : {};

      this.syncFormFromJson = true;
      this.ctaScreenForm = {
        ...defaultCtaScreenForm(),
        backgroundBlurEnabled: pickBool(c.backgroundBlurEnabled, true),
        backgroundColor: pickStr(c.backgroundColor),
        ctaTextColor: pickStr(c.ctaTextColor),
        ctaPlayBtnColor: pickStr(c.ctaPlayBtnColor),
        ctaPlayBtnTextColor: pickStr(c.ctaPlayBtnTextColor),
        ctaPlayBtnMaxWidth:
          c.ctaPlayBtnMaxWidth === undefined || c.ctaPlayBtnMaxWidth === null ? '' : String(c.ctaPlayBtnMaxWidth),
        ctaTitleVisible: pickBool(c.ctaTitleVisible, true),
        ctaDescriptionVisible: pickBool(c.ctaDescriptionVisible, true),
        ctaTitlePlacement: parsePlacement(c.ctaTitlePlacement, CTA_TITLE_PLACEMENTS, 'besideThumb'),
        playButtonPlacement: parsePlacement(c.playButtonPlacement, PLAY_BUTTON_PLACEMENTS, 'afterThumb'),
        displayAdMobileSizesJson: stringifySizes(c.displayAdMobileSizes, '[[300, 50]]'),
        displayAdDesktopSizesJson: stringifySizes(c.displayAdDesktopSizes, '[[300, 250], [300, 50]]'),
      };
      this.endScreenForm = {
        ...defaultEndScreenForm(),
        backgroundBlurEnabled: pickBool(e.backgroundBlurEnabled, true),
        backgroundColor: pickStr(e.backgroundColor),
        useCtaBackground: pickBool(e.useCtaBackground, false),
      };
      this.$nextTick(() => {
        this.syncFormFromJson = false;
        this.$nextTick(() => {
          this._lastPreviewAdSizesFingerprint = adSizesFingerprintFromCtaScreen(this.serializeCtaScreen());
          this.pushCustomUiToPreviewIframe();
        });
      });
    },
    serializeCtaScreen() {
      const o = {};
      o.backgroundBlurEnabled = !!this.ctaScreenForm.backgroundBlurEnabled;
      const bg = String(this.ctaScreenForm.backgroundColor || '').trim();
      if (bg) {
        o.backgroundColor = bg;
      }
      const tc = String(this.ctaScreenForm.ctaTextColor || '').trim();
      if (tc) {
        o.ctaTextColor = tc;
      }
      const pc = String(this.ctaScreenForm.ctaPlayBtnColor || '').trim();
      if (pc) {
        o.ctaPlayBtnColor = pc;
      }
      const ptc = String(this.ctaScreenForm.ctaPlayBtnTextColor || '').trim();
      if (ptc) {
        o.ctaPlayBtnTextColor = ptc;
      }
      const mwRaw = String(this.ctaScreenForm.ctaPlayBtnMaxWidth || '').trim().replace(/px$/i, '');
      if (mwRaw !== '') {
        const n = Number(mwRaw);
        if (!Number.isNaN(n) && Number.isInteger(n) && n >= 80 && n <= 600) {
          o.ctaPlayBtnMaxWidth = n;
        }
      }
      o.ctaTitleVisible = !!this.ctaScreenForm.ctaTitleVisible;
      o.ctaDescriptionVisible = !!this.ctaScreenForm.ctaDescriptionVisible;
      o.ctaTitlePlacement = parsePlacement(
        this.ctaScreenForm.ctaTitlePlacement,
        CTA_TITLE_PLACEMENTS,
        'besideThumb',
      );
      o.playButtonPlacement = parsePlacement(
        this.ctaScreenForm.playButtonPlacement,
        PLAY_BUTTON_PLACEMENTS,
        'afterThumb',
      );
      const mob = parseJsonSizePairs(this.ctaScreenForm.displayAdMobileSizesJson, 12, 728, 300);
      if (mob) {
        o.displayAdMobileSizes = mob;
      }
      const desk = parseJsonSizePairs(this.ctaScreenForm.displayAdDesktopSizesJson, 12, 728, 300);
      if (desk) {
        o.displayAdDesktopSizes = desk;
      }
      return o;
    },
    serializeEndScreen() {
      const o = {};
      o.backgroundBlurEnabled = !!this.endScreenForm.backgroundBlurEnabled;
      const bg = String(this.endScreenForm.backgroundColor || '').trim();
      if (bg) {
        o.backgroundColor = bg;
      }
      o.useCtaBackground = !!this.endScreenForm.useCtaBackground;
      return o;
    },
    rebuildJsonFromForm() {
      let base = {};
      try {
        const cur = JSON.parse(String(this.jsonText || '').trim() || '{}');
        if (cur && typeof cur === 'object' && !Array.isArray(cur)) {
          base = { ...cur };
        }
      } catch (e) {
        base = {};
      }
      const cta = this.serializeCtaScreen();
      const end = this.serializeEndScreen();
      base.ctaScreen = cta;
      base.endScreen = end;
      this.jsonText = JSON.stringify(base, null, 2);
    },
    async reload() {
      this.loading = true;
      this.loadError = '';
      this.formError = '';
      if (this.$msal && typeof this.$msal.ensureAuthenticated === 'function') {
        try {
          await this.$msal.ensureAuthenticated();
        } catch (e) {
          console.warn('ensureAuthenticated before syndication config load:', e);
        }
      }
      const url = `${config.apiUrl}/SyndicationConfig`;
      this.$http
        .get(url)
        .then((res) => {
          this.clients = normalizeSyndicationClientsBody(res.body);
        })
        .catch((err) => {
          console.error(err);
          const code = err.status;
          let msg =
            (err.body && err.body.message) || err.statusText || 'Failed to load syndication config.';
          if (code === 401) {
            msg =
              'Not authorized — Azure session may have expired. Sign in again or refresh the page.';
          }
          this.loadError = code != null && code !== '' ? `${msg} (HTTP ${code})` : msg;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    bumpIframePreview() {
      this.iframePreviewKey += 1;
    },
    buildCustomUiForPreviewPostMessage() {
      return {
        ctaScreen: this.serializeCtaScreen(),
        endScreen: this.serializeEndScreen(),
      };
    },
    getPreviewIframeEl() {
      return this.$refs.partnerPreviewIframe || null;
    },
    pushCustomUiToPreviewIframe() {
      if (!this.embedPreviewSrc || this.syncFormFromJson) {
        return;
      }
      const targetOrigin = this.embedPreviewOrigin;
      if (!targetOrigin) {
        return;
      }
      const iframe = this.getPreviewIframeEl();
      const win = iframe && iframe.contentWindow;
      if (!win) {
        return;
      }
      try {
        const payload = {
          type: 'syndication:setCustomUi',
          customUi: this.buildCustomUiForPreviewPostMessage(),
        };
        console.info('[syndication custom ui preview] postMessage', { targetOrigin, payload });
        win.postMessage(payload, targetOrigin);
      } catch (e) {
        console.error(e);
      }
    },
    resetCustomUiInPreviewIframe() {
      const targetOrigin = this.embedPreviewOrigin;
      if (!targetOrigin) {
        return;
      }
      const iframe = this.getPreviewIframeEl();
      const win = iframe && iframe.contentWindow;
      if (!win) {
        return;
      }
      try {
        const payload = { type: 'syndication:setCustomUi', customUi: null };
        console.info('[syndication custom ui preview] postMessage', { targetOrigin, payload });
        win.postMessage(payload, targetOrigin);
      } catch (e) {
        console.error(e);
      }
    },
    onPreviewIframeLoad() {
      this.$nextTick(() => {
        this.pushCustomUiToPreviewIframe();
      });
    },
    /** Coalesce rapid watcher fires (same macrotask); use fetch with credentials omit so admin Bearer is not sent to partner origin. */
    scheduleSyndicationCatalogFetch() {
      if (this._syndicationCatalogFetchTimer) {
        clearTimeout(this._syndicationCatalogFetchTimer);
      }
      this._syndicationCatalogFetchTimer = setTimeout(() => {
        this._syndicationCatalogFetchTimer = null;
        this.fetchSyndicationGamesForPreview();
      }, 0);
    },
    async fetchSyndicationGamesForPreview() {
      const logPrefix = '[syndication catalog preview]';

      if (this._syndicationCatalogAbortController) {
        this._syndicationCatalogAbortController.abort();
      }
      const controller = new AbortController();
      this._syndicationCatalogAbortController = controller;
      const { signal } = controller;

      const origin = originFromPartnerDomainField(this.domainLabel);
      if (!origin) {
        console.info(`${logPrefix} skip: no partner domain`);
        this.syndicationGamesForPreview = [];
        this._syndicationCatalogGamesRaw = null;
        this.syndicationGamesLoadError = 'Set partner domain to load the game list from the syndication catalog.';
        this.syndicationGamesLoading = false;
        if (this._syndicationCatalogAbortController === controller) {
          this._syndicationCatalogAbortController = null;
        }
        return;
      }
      const urls = syndicationCatalogUrlCandidates(origin);
      if (!urls.length) {
        console.info(`${logPrefix} skip: no catalog URLs`);
        this.syndicationGamesForPreview = [];
        this.syndicationGamesLoadError = '';
        this.syndicationGamesLoading = false;
        if (this._syndicationCatalogAbortController === controller) {
          this._syndicationCatalogAbortController = null;
        }
        return;
      }
      console.info(`${logPrefix} will try GET (in order):`, urls);
      this.syndicationGamesLoading = true;
      this.syndicationGamesLoadError = '';
      try {
        let payload = null;
        let lastDetail = '';
        for (let u = 0; u < urls.length; u += 1) {
          const catalogUrl = urls[u];
          try {
            console.info(`${logPrefix} GET`, catalogUrl);
            const res = await fetch(catalogUrl, {
              signal,
              credentials: 'omit',
              mode: 'cors',
            });
            console.info(`${logPrefix} response`, catalogUrl, res.status, res.statusText);
            if (!res.ok) {
              lastDetail = `HTTP ${res.status}`;
              continue;
            }
            const data = await res.json();
            if (!data || !Array.isArray(data.games) || data.games.length === 0) {
              lastDetail = 'empty catalog';
              console.info(`${logPrefix} no usable games array`, catalogUrl);
              continue;
            }
            payload = data;
            console.info(`${logPrefix} ok`, catalogUrl, `games=${data.games.length}`);
            this._syndicationCatalogGamesRaw = data.games;
            break;
          } catch (e) {
            if (e && e.name === 'AbortError') {
              console.info(`${logPrefix} aborted`, catalogUrl);
              return;
            }
            lastDetail = (e && e.message) || 'network';
            console.warn(`${logPrefix} fetch failed`, catalogUrl, e);
          }
        }
        if (signal.aborted) {
          return;
        }
        if (!payload) {
          console.warn(`${logPrefix} all candidates failed`, lastDetail || 'no catalog');
          throw new Error(lastDetail || 'no catalog');
        }
        const items = mapSyndicationCatalogGames(payload.games, origin, this.embedPreviewLocale);
        this.syndicationGamesForPreview = items;
        const slugs = new Set(items.map((i) => i.slug));
        if (!this.previewGameSlug || !slugs.has(this.previewGameSlug)) {
          const pick = items.find((x) => x.slug === 'blackjack') || items[0];
          if (pick) {
            this.previewGameSlug = pick.slug;
          }
        }
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return;
        }
        console.error(err);
        this.syndicationGamesForPreview = [];
        this._syndicationCatalogGamesRaw = null;
        const detail = err && err.message ? String(err.message) : '';
        this.syndicationGamesLoadError = detail
          ? `Could not load catalog from partner domain (${detail}). Expected JSON with { games: [...] } at GET ${origin}/syndication.`
          : `Could not load games from partner domain (GET ${origin}/syndication).`;
      } finally {
        if (this._syndicationCatalogAbortController === controller) {
          this.syndicationGamesLoading = false;
          this._syndicationCatalogAbortController = null;
        }
      }
    },
    persistSyndicationClients(nextClients, successTitle) {
      this.saving = true;
      const url = `${config.apiUrl}/SyndicationConfig`;
      return this.$http
        .post(url, nextClients, {
          headers: { 'Content-Type': 'application/json' },
        })
        .then(() => ClearCacheService.clearSyndicationClientConfigCache())
        .then(() => {
          this.$set(this, 'clients', nextClients);
          this.$swal.fire({
            icon: 'success',
            title: successTitle,
            timer: 2000,
            showConfirmButton: false,
          });
          this.$router.push('/syndication-config');
        })
        .catch((err) => {
          console.error(err);
          this.$swal.fire({
            icon: 'error',
            title: 'Save failed',
            text: this.httpErrorMessage(err),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async confirmDisableCustomUi() {
      const p = this.partner;
      if (!p || this.saving) {
        return;
      }
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Disable custom UI?',
        html: 'This clears custom UI for this partner, saves immediately, and clears the CDN cache.',
        showCancelButton: true,
        confirmButtonText: 'Disable and save',
        cancelButtonText: 'Cancel',
        focusCancel: true,
      });
      if (!isConfirmed) {
        return;
      }
      const existingKey = findCustomUiPropertyKey(p);
      const outKey = existingKey || 'customUi';
      const nextClients = this.clients.map((c, i) => {
        if (i !== this.partnerIndex) {
          return c;
        }
        return { ...c, [outKey]: null };
      });
      this.persistSyndicationClients(nextClients, 'Custom UI disabled');
    },
    save() {
      this.formError = '';
      const p = this.partner;
      if (!p) {
        return;
      }
      const parsedResult = parseCustomUiObjectFromJsonText(this.jsonText);
      if (!parsedResult.ok) {
        this.formError = parsedResult.error;
        return;
      }
      const { value: parsed } = parsedResult;

      const existingKey = findCustomUiPropertyKey(p);
      const outKey = existingKey || 'customUi';

      const nextClients = this.clients.map((c, i) => {
        if (i !== this.partnerIndex) {
          return c;
        }
        return { ...c, [outKey]: parsed };
      });

      this.persistSyndicationClients(nextClients, 'Saved');
    },
  },
};
</script>

<style scoped>
.partner-ui-card-head {
  margin-bottom: 6px !important;
  line-height: 1.25;
}
.partner-ui-settings-stack.partner-ui-compact-form {
  gap: 10px;
}
.partner-ui-compact-form :deep(.v-text-field--outlined:not(.v-textarea) .v-input__slot),
.partner-ui-compact-form :deep(.v-select--outlined .v-input__slot) {
  min-height: 36px !important;
}
.partner-ui-compact-form :deep(.v-text-field--outlined.v-textarea .v-input__slot) {
  min-height: 40px !important;
}
.partner-ui-compact-form :deep(.v-input--selection-controls) {
  margin-top: 2px !important;
  padding-top: 0 !important;
}
.partner-ui-compact-form :deep(.v-input--selection-controls .v-input__slot) {
  min-height: 32px !important;
}
.partner-ui-compact-form .syndication-color-picker :deep(.v-input__slot) {
  min-height: 34px !important;
  padding: 2px 4px !important;
}
.partner-ui-compact-form .syndication-color-picker :deep(input[type='color']) {
  height: 26px;
  min-height: 26px;
}
.mono-json :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.35;
  height: fit-content;
}
.partner-ui-compact-form .mono-json :deep(textarea) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
.partner-ui-embed-card {
  height: fit-content;
}
.partner-ui-preview-toolbar {
  row-gap: 4px;
  column-gap: 4px;
}
.partner-ui-preview-game-field {
  flex: 1 1 200px;
  min-width: 0;
  max-width: 100%;
}
.partner-ui-preview-game-select :deep(.v-messages) {
  margin-bottom: 0;
}
@media (min-width: 960px) {
  .partner-ui-main-row {
    align-items: stretch;
  }
  .partner-ui-embed-sticky {
    position: sticky;
    top: 12px;
    z-index: 1;
  }
  .partner-ui-settings-col {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    min-height: calc(100vh - 200px);
  }
  .partner-ui-settings-stack {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 6px;
  }
}
.partner-ui-embed-preview-block {
  width: 100%;
}
.partner-ui-preview-canvas {
  border-radius: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.06);
}
.partner-ui-preview-viewport {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}
.partner-ui-preview-viewport--pc {
  max-width: min(100%, 960px);
}
.partner-ui-preview-viewport--pc .partner-ui-aspect-wrap {
  padding-top: 75%; /* 4:3 */
}
.partner-ui-preview-viewport--tablet {
  max-width: 768px;
}
.partner-ui-preview-viewport--phone {
  max-width: 390px;
}
.partner-ui-preview-viewport--phone .partner-ui-aspect-wrap {
  /* Taller than default 4:3 — narrow phone frame was too squat (~9:16 portrait). */
  padding-top: 178%;
}
.partner-ui-aspect-wrap {
  position: relative;
  width: 100%;
  padding-top: 75%;
  border-radius: 4px;
  overflow: hidden;
  background: #0a0a0a;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
/* Native color input: make a visible swatch, not a thin line */
.syndication-color-picker {
  flex: 0 0 48px;
  max-width: 48px;
}
.syndication-color-picker :deep(.v-input__slot) {
  min-height: 40px !important;
  padding: 4px 6px !important;
}
.syndication-color-picker :deep(input[type='color']) {
  display: block;
  width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}
.syndication-color-picker :deep(input[type='color'])::-webkit-color-swatch-wrapper {
  padding: 3px;
}
.syndication-color-picker :deep(input[type='color'])::-webkit-color-swatch {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.28);
}
.syndication-color-picker :deep(input[type='color'])::-moz-color-swatch {
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.28);
}
.partner-ui-preview-iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
